import { createEntityAdapter, SerializedError } from '@reduxjs/toolkit';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';

import { RequestStatus } from '@app/types';
import {
  CompanyPolicyListItem as CompanyPolicy,
  EmployeePolicyListItem as EmployeePolicy,
  GetPolicyWithVersionsResponse as Policy,
  PolicyEmployee as Employee,
  PolicyCompany,
  FilterSelectOption,
  PolicyCategoryItem,
} from '@app/gen/policy';
import { GetUnitsTreeResponse, GetUnitTypesResponse } from '@app/gen/settings';

export interface PolicyState {
  status: RequestStatus;
  currentIds: string[];
  isNotLastItems: boolean;
  error: AppError | null;
  filters: Filter[] | null;
  totalCount: number | null;
  totalVersionCount: number | null;
  totalUnacceptedCount: number | null;
  totalUnacceptedVersionCount: number | null;
  unpublishedVersionsCount: number | null;
  creation: {
    status: RequestStatus;
    error: AppError | null;
  };
  printPolicy: {
    status: RequestStatus;
    error: AppError | null;
  };
  creationData: {
    status: RequestStatus;
    categories: PolicyCategoryItem[];
    companies: PolicyCompany[];
    positions: {
      status: RequestStatus;
      hasPositions: boolean;
    };
    unitTypes: {
      status: RequestStatus;
      values: GetUnitTypesResponse;
    };
    employees: {
      status: RequestStatus;
      hasEmployees: boolean;
    };
    unitsTree: Partial<GetUnitsTreeResponse>;
    govDocumentOptions: FilterSelectOption[];
    error: AppError | null;
  };
  detail: {
    entity: Policy | null;
    employees: {
      status: RequestStatus;
      entities: Employee[];
      error: AppError | null;
      hasMore: boolean;
    };
    unitTypes: GetUnitTypesResponse;
  };
  edit: {
    status: RequestStatus;
    error: AppError | null;
    companies: {
      status: RequestStatus;
      items: PolicyCompany[];
    };
    categories: PolicyCategoryItem[];
    unitTypes: {
      values: GetUnitTypesResponse;
      status: RequestStatus;
    };
    unitsTree: Partial<GetUnitsTreeResponse>;
    unitsTrees: Record<string, Partial<GetUnitsTreeResponse>>;
  };
  categories: {
    status: RequestStatus;
    error: AppError | null;
    items: PolicyCategoryItem[];
    total: number;
    parentOptions: FilterSelectOption[];
    deleteStatus: RequestStatus;
    creationData: {
      accountsStatus: RequestStatus;
      accounts: FilterSelectOption[];
      categoriesStatus: RequestStatus;
      categoriesByAccountId: Record<string, PolicyCategoryItem[]>;
    };
  };
  childCompanies: {
    status: 'idle' | 'loading' | 'complete' | 'failed';
    items: PolicyCompany[];
  };
}

export const initialState: PolicyState = {
  status: 'idle',
  currentIds: [],
  error: null,
  isNotLastItems: true,
  filters: null,
  totalCount: null,
  totalVersionCount: null,
  totalUnacceptedCount: null,
  totalUnacceptedVersionCount: null,
  unpublishedVersionsCount: null,
  creation: {
    status: 'idle',
    error: null,
  },
  printPolicy: {
    status: 'idle',
    error: null,
  },
  creationData: {
    status: 'idle',
    categories: [],
    companies: [],
    error: null,
    govDocumentOptions: [],
    positions: {
      status: 'idle',
      hasPositions: false,
    },
    employees: {
      status: 'idle',
      hasEmployees: false,
    },
    unitTypes: {
      values: {
        types: [],
      },
      status: 'idle',
    },
    unitsTree: {},
  },
  detail: {
    entity: null,
    employees: {
      status: 'idle',
      entities: [],
      error: null,
      hasMore: true,
    },
    unitTypes: {
      types: [],
    },
  },
  edit: {
    status: 'idle',
    error: null,
    companies: {
      status: 'idle',
      items: [],
    },
    categories: [],
    unitTypes: {
      values: {
        types: [],
      },
      status: 'idle',
    },
    unitsTree: {},
    unitsTrees: {},
  },
  categories: {
    status: 'idle',
    error: null,
    items: [],
    total: 0,
    parentOptions: [],
    deleteStatus: 'idle',
    creationData: {
      accountsStatus: 'idle',
      accounts: [],
      categoriesStatus: 'idle',
      categoriesByAccountId: {},
    },
  },
  childCompanies: {
    status: 'idle',
    items: [],
  },
};

export const startLoading = (state: PolicyState) => {
  state.status = 'loading';
  state.error = null;
  state.detail = {
    entity: null,
    employees: {
      status: 'loading',
      entities: [],
      error: null,
      hasMore: true,
    },
    unitTypes: {
      types: [],
    },
  };
  state.currentIds = [];
};

export const completeLoading = (state: PolicyState) => {
  state.status = 'complete';
  state.error = null;
};

export const setError = (
  state: PolicyState,
  { payload, error }: { payload?: AppError; error: SerializedError },
) => {
  state.status = 'failed';
  state.error =
    payload ||
    ({
      info: (error && error.message) || 'Unknown error',
      status: 500,
      source: 'client',
      title: 'Internal client error',
    } as AppError);
};

export const policyAdapter = createEntityAdapter<
  CompanyPolicy | EmployeePolicy,
  string
>({
  selectId: (policy) => {
    if ('policy_version_id' in policy && 'employee_id' in policy) {
      return `${policy.policy_version_id}${policy.employee_id}`;
    }

    return policy.policy_id;
  },
});
